import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import {
  AiMeetingStyleSelector,
  Button,
  Card,
  DEFAULT_MEETING_TYPE,
  MEETING_STYLE_UNAVAILABLE_MESSAGE,
  Input,
  MEETING_TYPE_FIELD_OPTIONS,
  ProgrammingLanguagesSelect,
  Select,
  SettingsSwitch,
  formatAddonMoney,
  isInterviewMeetingType,
  useAiMeetingStyle,
} from '@copilot/ui';
import Icon from '../components/common/Icon';
import { Field } from '../components/common/Field';
import CompanySelector from '../components/meeting/CompanySelector';
import PreviousMeetingsPicker from '../components/meeting/PreviousMeetingsPicker';
import DocumentSelector from '../components/documents/DocumentSelector';
import {
  canCaptureMeetingTab,
  defaultAudioSourceForDevice,
  FIXED_AUDIO_SOURCE_LABEL,
} from '../constants/audioSources';
import {
  MEETING_CONTEXT_MODES,
  MEETING_CONTEXT_MODE_OPTIONS,
} from '../constants/meetingContext';
import {
  AI_ANSWER_LANGUAGE_OPTIONS,
  AI_ANSWER_LANGUAGE_SAME_AS_TRANSLATION,
  CO_PILOT_MODES,
  LANGUAGES,
  SOURCE_LANGUAGE_AUTO,
  SPOKEN_LANGUAGE_OPTIONS,
} from '../constants/languages';
import { getUserFriendlyError } from '../utils/errors';
import { reportError } from '../services/errorTracking';
import { useAiPersonalizationApi } from '../hooks/useAiPersonalizationApi';
import { useAuth } from '../hooks/useAuth';
import * as capabilitiesApi from '../services/capabilitiesApi';
import * as meetingApi from '../services/meetingApi';
import {
  PAY_PER_MEETING,
  USAGE_ERROR_CODES,
  isUsageLimitError,
  payPerMeetingBuyLabel,
} from '../services/pricingApi';
import * as meetingPassApi from '../services/meetingPassApi';
import * as billingApi from '../services/billingApi';
import { rememberMicrophoneChoice } from '../audioClient';

// The microphone a new meeting starts on: the one saved in Settings if it is
// still there, otherwise the first one the browser lists. The selector below
// shows which, and the user can change it before starting.
function initialMicrophoneId(mics, savedId) {
  if (savedId && mics.some((m) => m.deviceId === savedId)) {
    return savedId;
  }

  return mics.length > 0 ? mics[0].deviceId : '';
}

function StartMeetingPage() {
  const { user } = useAuth();
  // AI Response Style: the account default preselected; a different choice
  // applies to this meeting only (shown only where the backend enables it).
  const aiPersonalizationApi = useAiPersonalizationApi();
  const aiStyle = useAiMeetingStyle(aiPersonalizationApi, user?.id);
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [contextMode, setContextMode] = useState(MEETING_CONTEXT_MODES.AUTOMATIC);
  const [contextMeetingIds, setContextMeetingIds] = useState([]);
  const [isMeetingPickerOpen, setIsMeetingPickerOpen] = useState(false);
  const [microphoneId, setMicrophoneId] = useState('');
  // Every microphone the browser lists, so the user can see and choose which
  // one KrishvaQ records for their own speech.
  const [microphones, setMicrophones] = useState([]);
  const [savedMicrophoneGone, setSavedMicrophoneGone] = useState(false);
  const [microphoneNote, setMicrophoneNote] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState(SOURCE_LANGUAGE_AUTO);
  const [targetLanguage, setTargetLanguage] = useState(
    user?.preferred_language || 'en'
  );
  const [aiAnswerLanguage, setAiAnswerLanguage] = useState(
    AI_ANSWER_LANGUAGE_SAME_AS_TRANSLATION
  );
  const [mode, setMode] = useState('translation_ai');
  // AI answer context: what kind of meeting it is, and which languages code
  // in answers should prefer (none = picked from each question).
  const [meetingType, setMeetingType] = useState(DEFAULT_MEETING_TYPE);
  const [programmingLanguages, setProgrammingLanguages] = useState([]);
  // Meeting mode only: "Show Live Transcript". OFF by default. It only decides
  // whether the transcript and its translation are displayed - speech
  // recognition runs either way, because automatic AI answers need it. An
  // Interview has no such choice (it never shows a transcript), so the switch
  // and the spoken-language field are hidden for it.
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(false);
  const isInterview = isInterviewMeetingType(meetingType);
  // Meeting mode with the live transcript hidden: speech is still recognized
  // for AI answers, but no transcript or translation is displayed.
  const transcriptHidden = !isInterview && !transcriptionEnabled;
  // Translation is only shown with the live transcript: an Interview, and a
  // Meeting with Show Live Transcript off, have no translated text to choose a
  // language for. The value is kept (and still sent) for AI answers set to
  // "Same as Translation".
  const translationHidden = isInterview || transcriptHidden;
  const [error, setError] = useState('');
  // A plan/usage refusal is not an ordinary form error: it is not
  // fixable by editing this form, so it gets an upgrade link instead of
  // a plain red message.
  const [limitError, setLimitError] = useState(null);
  // Set when the refusal was the monthly meeting count - the one a
  // Pay Per Meeting purchase resolves.
  const [canBuyMeeting, setCanBuyMeeting] = useState(false);
  const [passProduct, setPassProduct] = useState(null);
  // Plan extra meetings, when the server says this user may buy them now
  // (an entitlement with addon_checkout_available). Never decided here.
  const [addonOffer, setAddonOffer] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The TRUE capabilities of the currently-configured backend
  // STT/translation/TTS providers — null while loading, in which case
  // the dropdowns below render from the static constants/languages.js
  // fallback instead of blocking the page on this fetch.
  const [capabilities, setCapabilities] = useState(null);

  // Only documents explicitly selected for this meeting live on the
  // page. The user's full library is searched inside DocumentSelector so
  // the meeting form does not render or eagerly fetch a long list.
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isDocumentSelectorOpen, setIsDocumentSelectorOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    capabilitiesApi
      .getLanguageCapabilities()
      .then((data) => {
        if (!cancelled) setCapabilities(data);
      })
      .catch(() => {
        // Best-effort: keep the static fallback list if this fails
        // (e.g. offline) rather than blocking meeting creation.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const spokenLanguageOptions = capabilities
    ? [
        ...(capabilities.stt.supports_auto
          ? [{ code: SOURCE_LANGUAGE_AUTO, label: 'Auto / Multilingual' }]
          : []),
        ...capabilities.stt.languages.map((l) => ({ code: l.code, label: l.label })),
      ]
    : SPOKEN_LANGUAGE_OPTIONS;

  const translateToOptions = capabilities
    ? capabilities.translation.target_languages
    : LANGUAGES;

  const aiAnswerLanguageOptions = capabilities
    ? [
        {
          value: AI_ANSWER_LANGUAGE_SAME_AS_TRANSLATION,
          label: 'Same as Translation',
        },
        ...capabilities.ai_answer.languages.map((l) => ({
          value: l.code,
          label: l.label,
        })),
      ]
    : AI_ANSWER_LANGUAGE_OPTIONS;

  const selectedDocumentIds = selectedDocuments.map((document) => document.id);

  const removeSelectedDocument = (documentId) => {
    setSelectedDocuments((current) =>
      current.filter((document) => document.id !== documentId)
    );
  };

  // Once real capabilities load, a previously-selected/saved language
  // that the active provider no longer supports (or never did) must
  // fall back to a sane supported default instead of silently
  // submitting an invalid value or leaving the form on a option that
  // no longer renders.
  useEffect(() => {
    if (!capabilities) return;

    if (!spokenLanguageOptions.some((opt) => opt.code === sourceLanguage)) {
      setSourceLanguage(
        capabilities.stt.supports_auto
          ? SOURCE_LANGUAGE_AUTO
          : spokenLanguageOptions[0]?.code || SOURCE_LANGUAGE_AUTO
      );
    }

    if (!translateToOptions.some((opt) => opt.code === targetLanguage)) {
      setTargetLanguage(translateToOptions[0]?.code || 'en');
    }

    if (!aiAnswerLanguageOptions.some((opt) => opt.value === aiAnswerLanguage)) {
      setAiAnswerLanguage(AI_ANSWER_LANGUAGE_SAME_AS_TRANSLATION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capabilities]);

  useEffect(() => {
    const prefs = user?.preferences || {};

    setSourceLanguage(prefs.default_source_language || SOURCE_LANGUAGE_AUTO);
    setTargetLanguage(
      prefs.default_target_language || user?.preferred_language || 'en'
    );
    setAiAnswerLanguage(
      prefs.default_ai_answer_language || AI_ANSWER_LANGUAGE_SAME_AS_TRANSLATION
    );
    setMode(prefs.default_mode || 'translation_ai');
  }, [user]);

  useEffect(() => {
    // Enumeration and preference application must happen together, not
    // as two independent effects: applying the saved microphone
    // preference and then unconditionally overwriting it with
    // mics[0].deviceId once the device list loads was a real bug —
    // the saved choice was always clobbered a moment after mount.
    async function loadMicrophones() {
      try {
        // Listing device labels requires a prior getUserMedia permission
        // grant; if it hasn't been granted yet the labels will just be
        // blank until the user starts a meeting once.
        const devices = await navigator.mediaDevices.enumerateDevices();
        const mics = devices.filter((d) => d.kind === 'audioinput');

        const savedId = user?.preferences?.default_microphone_id;
        const startId = initialMicrophoneId(mics, savedId);

        setMicrophones(mics);
        setSavedMicrophoneGone(!!savedId && startId !== savedId);
        setMicrophoneId(startId);
      } catch (_) {
        setMicrophones([]);
        setMicrophoneId('');
      }
    }

    loadMicrophones();
  }, [user]);

  // A headset switched on or plugged in while this page is open changes the
  // list. Only the list is refreshed: the selection stays what the user chose,
  // and a selected device that went away is flagged, never swapped for another.
  useEffect(() => {
    const mediaDevices = navigator.mediaDevices;

    if (!mediaDevices || typeof mediaDevices.addEventListener !== 'function') {
      return undefined;
    }

    const refreshMicrophones = async () => {
      try {
        const devices = await mediaDevices.enumerateDevices();
        setMicrophones(devices.filter((d) => d.kind === 'audioinput'));
      } catch (_) {
        /* the list simply stays as it was */
      }
    };

    mediaDevices.addEventListener('devicechange', refreshMicrophones);

    return () =>
      mediaDevices.removeEventListener('devicechange', refreshMicrophones);
  }, []);

  // Browsers list microphones without names (and without usable ids) until the
  // site has been allowed to use one. Allowing it here, on request, is what
  // makes a choice between several microphones possible at all.
  const showMicrophoneNames = async () => {
    setMicrophoneNote('');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter((d) => d.kind === 'audioinput');
      const savedId = user?.preferences?.default_microphone_id;

      setMicrophones(mics);
      setSavedMicrophoneGone(
        !!savedId && initialMicrophoneId(mics, savedId) !== savedId
      );
      setMicrophoneId((current) => current || initialMicrophoneId(mics, savedId));
    } catch (_) {
      setMicrophoneNote(
        'Microphone access was not allowed, so the microphone names cannot be shown. Allow it in your browser to choose between microphones.'
      );
    }
  };

  const microphoneNamesHidden =
    microphones.length > 0 && microphones.every((device) => !device.label);
  const selectedMicrophoneListed = microphones.some(
    (device) => device.deviceId === microphoneId
  );

  // Phone and tablet browsers cannot capture a meeting tab (no
  // getDisplayMedia), so what is promised below - and what the meeting is
  // created as - depends on the browser, not on a setting.
  const tabCaptureAvailable = canCaptureMeetingTab();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLimitError(null);

    if (!title.trim()) {
      setError('Meeting name is required.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Speech provider (Google/Sarvam) is deliberately not part of this
      // request - it's resolved entirely on the backend from
      // DEFAULT_STT_PROVIDER/DEFAULT_TTS_PROVIDER, not chosen here.
      const meeting = await meetingApi.createMeeting({
        title: title.trim(),
        sourceLanguage,
        targetLanguage,
        aiAnswerLanguage,
        mode,
        // Fixed: every Web meeting is Meeting / Browser Tab (it also records
        // the user's microphone internally). A saved preference of
        // 'microphone' is ignored. The one exception is a browser that
        // cannot capture a tab at all (phones and tablets): there the
        // meeting is a microphone meeting, which is the capture that
        // browser really has.
        audioSource: defaultAudioSourceForDevice(),
        documentIds: selectedDocumentIds,
        // One entry per selected document: ON only where the user switched it
        // on, and never for a document that is not ready yet.
        documentAiSettings: selectedDocuments.map((document) => ({
          document_id: document.id,
          use_for_ai_answers:
            document.use_for_ai_answers === true &&
            document.processing_status === 'completed',
        })),
        companyId: companyId || undefined,
        contextMode,
        contextMeetingIds,
        meetingType,
        programmingLanguages,
        transcriptionEnabled: isInterview ? undefined : transcriptionEnabled,
        aiPersonalizationOverride: aiStyle.override || undefined,
      });

      // Activate over REST before opening /ws/audio so lifecycle
      // conflicts surface as the backend's structured 409 response
      // instead of a browser-only websocket 403.
      const activeMeeting = await meetingApi.updateMeeting(meeting.id, {
        status: 'active',
      });

      // So the same device can be found again by name if a Bluetooth headset
      // comes back under a new id between here and the capture.
      const chosenDevice = microphones.find(
        (device) => device.deviceId === microphoneId
      );
      rememberMicrophoneChoice(microphoneId, chosenDevice?.label || '');

      navigate(`/app/meeting/${activeMeeting.id}/live`, {
        state: { microphoneId },
      });
    } catch (err) {
      if (isUsageLimitError(err)) {
        setLimitError(err.message);
        // Out of monthly meetings: a single extra meeting can be bought
        // without changing plan. Only the offer is prepared here - the
        // purchase starts when the user follows the link.
        const outOfMeetings = err.code === USAGE_ERROR_CODES.MEETING_COUNT_LIMIT_REACHED;
        setCanBuyMeeting(outOfMeetings);
        setAddonOffer(null);
        if (outOfMeetings) {
          meetingPassApi
            .getProduct()
            .then(setPassProduct)
            .catch(() => {});
          billingApi
            .getEntitlement()
            .then((entitlement) => setAddonOffer(entitlement?.addon_checkout_available ? entitlement : null))
            .catch(() => {});
        }
      } else if (
        err.code === 'PROFILE_NOT_FOUND' ||
        err.code === 'INVALID_SELECTION' ||
        err.code === 'INVALID_BUILTIN_STYLE'
      ) {
        // The chosen style was refused (e.g. that profile was deleted meanwhile):
        // nothing was created. Show what is offered now and let them choose again.
        setError(MEETING_STYLE_UNAVAILABLE_MESSAGE);
        aiStyle.reload();
      } else {
        // Unexpected start failures only (usage limits above are expected).
        reportError('meeting_start', err);
        setError(getUserFriendlyError(err, 'Unable to start meeting. Please try again.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="start-meeting-wrap">
      <div className="start-meeting-hero">
        <div className="start-meeting-hero-copy">
          <span className="start-meeting-eyebrow">
            <Icon name="sparkles" size={14} />
            Meeting launch
          </span>
          <h2>Start a Meeting</h2>
          <p>
            Choose your audio source, language flow, and KrishvaQ mode. Once started,
            the live workspace will handle transcription, translation, answers, and notes.
          </p>
        </div>
        <div className="start-meeting-signal" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>
      </div>

      <div className="start-meeting-layout">
        <Card interactive className="start-meeting-card">
          <div className="start-meeting-card-header">
            <div>
              <span className="panel-kicker">Setup</span>
              <h3>Meeting configuration</h3>
            </div>
            <span className="setup-status-pill">
              <Icon name="check-circle" size={15} />
              Ready
            </span>
          </div>

          <form className="form-grid start-meeting-form" onSubmit={handleSubmit}>
            <section className="meeting-field-section">
              <div className="meeting-field-section-header">
                <span className="meeting-field-section-icon" aria-hidden="true">
                  <Icon name="meeting" size={17} />
                </span>
                <div>
                  <span className="panel-kicker">Meeting profile</span>
                  <h4>Name and related context</h4>
                </div>
              </div>

              <div className="meeting-field-stack">
                <div className="start-form-two">
                  <Field label="Meeting Name" htmlFor="meeting-title">
                    <Input
                      id="meeting-title"
                      placeholder="UK Client Product Discussion"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                    />
                  </Field>

                  <Field label="Related To (Optional)" htmlFor="meeting-company">
                    <CompanySelector value={companyId} onChange={setCompanyId} />
                    <p className="field-help">
                      Connect this meeting with a person, company, client, or previous
                      conversation so KrishvaQ can remember relevant context.
                    </p>
                  </Field>
                </div>

                <div className="start-form-two">
                  <Field label="Meeting Type" htmlFor="meeting-type">
                    <Select
                      id="meeting-type"
                      value={meetingType}
                      onChange={(e) => setMeetingType(e.target.value)}
                    >
                      {MEETING_TYPE_FIELD_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                    <p className="field-help">
                      Guides how KrishvaQ frames its answers. It never stops an
                      answer - a coding question still gets code.
                    </p>
                  </Field>

                  <Field
                    label="Programming Languages (Optional)"
                    htmlFor="meeting-programming-languages"
                  >
                    <ProgrammingLanguagesSelect
                      id="meeting-programming-languages"
                      value={programmingLanguages}
                      onChange={setProgrammingLanguages}
                    />
                    <p className="field-help">
                      Used for code in AI answers. Leave empty and KrishvaQ picks
                      the language from each question.
                    </p>
                  </Field>
                </div>

                <AiMeetingStyleSelector
                  style={aiStyle}
                  disabled={isSubmitting}
                />

                {!isInterview && (
                  <div className="meeting-transcription-toggle">
                    <SettingsSwitch
                      title="Show Live Transcript"
                      description={
                        transcriptionEnabled
                          ? 'Display the live transcript and translated text.'
                          : 'Hide the live transcript. KrishvaQ still listens to detect questions and write AI answers automatically.'
                      }
                      checked={transcriptionEnabled}
                      onChange={setTranscriptionEnabled}
                    />
                  </div>
                )}

                <Field label="Previous Context">
                  <div className="meeting-context-mode-options">
                    {MEETING_CONTEXT_MODE_OPTIONS.map((option) => (
                      <label
                        key={option.value}
                        className={`meeting-context-mode-option ${
                          contextMode === option.value ? 'selected' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="meeting-context-mode"
                          value={option.value}
                          aria-label={option.label}
                          checked={contextMode === option.value}
                          onChange={() => setContextMode(option.value)}
                        />
                        <span className="meeting-context-check" aria-hidden="true">
                          <Icon name="check" size={14} />
                        </span>
                        <span>
                          <strong>{option.label}</strong>
                          <span className="field-help">{option.description}</span>
                        </span>
                      </label>
                    ))}
                  </div>

                  {contextMode === MEETING_CONTEXT_MODES.MANUAL && (
                    <div className="meeting-context-manual-picker">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setIsMeetingPickerOpen(true)}
                      >
                        <Icon name="plus" size={17} />
                        Select Previous Meetings
                      </Button>
                      <p className="field-help">
                        {contextMeetingIds.length === 0
                          ? 'No previous meetings selected'
                          : `Selected: ${contextMeetingIds.length} meeting${
                              contextMeetingIds.length === 1 ? '' : 's'
                            }`}
                      </p>
                    </div>
                  )}

                  {isMeetingPickerOpen && (
                    <PreviousMeetingsPicker
                      selectedMeetingIds={contextMeetingIds}
                      onChange={setContextMeetingIds}
                      onClose={() => setIsMeetingPickerOpen(false)}
                    />
                  )}
                </Field>
              </div>
            </section>

            <section className="meeting-field-section">
              <div className="meeting-field-section-header">
                <span className="meeting-field-section-icon" aria-hidden="true">
                  <Icon name="language" size={17} />
                </span>
                <div>
                  <span className="panel-kicker">Capture and language</span>
                  <h4>Audio, translation, and AI mode</h4>
                </div>
              </div>

              <div className="start-form-two">
                <Field label="Audio Source">
                  {/* Fixed, not a control: there is one mode. The user's
                      microphone is used internally for their own speech. */}
                  <div className="form-static-value" data-testid="audio-source-value">
                    {tabCaptureAvailable ? FIXED_AUDIO_SOURCE_LABEL : 'Microphone'}
                  </div>
                </Field>

                <Field label="Meeting Audio">
                  {tabCaptureAvailable ? (
                    <p className="field-help meeting-audio-help">
                      You&apos;ll select your Google Meet / Teams Web / Zoom Web
                      tab after opening the Live Meeting workspace. Your
                      microphone is used too, so your own speech is included.
                    </p>
                  ) : (
                    <p className="field-help meeting-audio-help">
                      This browser can&apos;t share a meeting tab (phone and
                      tablet browsers can&apos;t), so KrishvaQ will listen
                      through your microphone. Put the meeting on speaker so
                      the microphone can hear the other participants.
                    </p>
                  )}
                </Field>
              </div>

              <div className="start-form-two">
                <Field label="Your microphone" htmlFor="meeting-microphone">
                  <Select
                    id="meeting-microphone"
                    value={microphoneId}
                    onChange={(e) => setMicrophoneId(e.target.value)}
                  >
                    {microphoneNamesHidden || microphones.length === 0 ? (
                      <option value="">System default microphone</option>
                    ) : (
                      <>
                        {microphoneId && !selectedMicrophoneListed && (
                          <option value={microphoneId}>
                            Selected microphone (not connected)
                          </option>
                        )}
                        {microphones.map((device, index) => (
                          <option
                            key={`${device.deviceId}-${index}`}
                            value={device.deviceId}
                          >
                            {device.label || `Microphone ${index + 1}`}
                          </option>
                        ))}
                      </>
                    )}
                  </Select>

                  {microphoneNamesHidden && (
                    <>
                      <p className="field-help">
                        Your browser hides microphone names until you allow
                        microphone access.
                      </p>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={showMicrophoneNames}
                      >
                        Show microphone names
                      </Button>
                    </>
                  )}

                  {microphoneId &&
                    !selectedMicrophoneListed &&
                    !microphoneNamesHidden &&
                    microphones.length > 0 && (
                      <p className="field-help">
                        The selected microphone is not connected. KrishvaQ will
                        not record a different one instead - reconnect it or
                        choose another.
                      </p>
                    )}

                  {savedMicrophoneGone && selectedMicrophoneListed && (
                    <p className="field-help">
                      The microphone saved in Settings is not connected, so the
                      first one listed is selected. Choose the one you want.
                    </p>
                  )}

                  {microphoneNote && (
                    <p className="field-help">{microphoneNote}</p>
                  )}

                  <p className="field-help">
                    KrishvaQ records this microphone for your own speech.
                    Google Meet has its own microphone setting - use the same
                    headset in both. An entry starting with &quot;Default&quot;
                    follows whichever microphone your computer treats as its
                    default, which may not be your headset.
                  </p>
                </Field>
              </div>

              <div className="start-form-two">
                <Field label="Spoken Language" htmlFor="meeting-source-lang">
                  <Select
                    id="meeting-source-lang"
                    value={sourceLanguage}
                    onChange={(e) => setSourceLanguage(e.target.value)}
                  >
                    {spokenLanguageOptions.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.label}
                      </option>
                    ))}
                  </Select>
                </Field>

                {!translationHidden && (
                  <Field label="Translate To" htmlFor="meeting-target-lang">
                    <Select
                      id="meeting-target-lang"
                      value={targetLanguage}
                      onChange={(e) => setTargetLanguage(e.target.value)}
                    >
                      {translateToOptions.map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}
              </div>

              <div className="start-form-two">
                <Field label="AI Answer Language" htmlFor="meeting-ai-answer-lang">
                  <Select
                    id="meeting-ai-answer-lang"
                    value={aiAnswerLanguage}
                    onChange={(e) => setAiAnswerLanguage(e.target.value)}
                  >
                    {aiAnswerLanguageOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {translationHidden &&
                        opt.value === AI_ANSWER_LANGUAGE_SAME_AS_TRANSLATION
                          ? `Default (${
                              translateToOptions.find((l) => l.code === targetLanguage)
                                ?.label || targetLanguage
                            })`
                          : opt.label}
                      </option>
                    ))}
                  </Select>
                </Field>

                <Field label="KrishvaQ Mode" htmlFor="meeting-mode">
                  <Select
                    id="meeting-mode"
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                  >
                    {CO_PILOT_MODES.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            </section>

            <section className="meeting-field-section">
              <div className="meeting-field-section-header">
                <span className="meeting-field-section-icon" aria-hidden="true">
                  <Icon name="brain" size={17} />
                </span>
                <div>
                  <span className="panel-kicker">Knowledge sources</span>
                  <h4>AI documents</h4>
                </div>
              </div>

              <Field label="AI Documents (optional)">
                <section className="ai-context-panel">
                  <div className="ai-context-header">
                    <span className="ai-context-icon" aria-hidden="true">
                      <Icon name="brain" size={18} />
                    </span>
                    <div>
                      <span className="field-subheading">AI Context</span>
                      <p className="field-help">
                        Choose documents KrishvaQ should use for this meeting.
                      </p>
                    </div>
                  </div>

                  <div className="ai-context-selected">
                    <div className="ai-context-selected-header">
                      <div>
                        <span className="ai-context-label">Selected Documents</span>
                        <p className="field-help">
                          {selectedDocuments.length === 0
                            ? 'No documents selected'
                            : `${selectedDocuments.length} document${selectedDocuments.length === 1 ? '' : 's'} selected`}
                        </p>
                      </div>
                    </div>

                    {selectedDocuments.length === 0 ? (
                      <div className="ai-context-empty">
                        <Icon name="file-text" size={18} />
                        <p>Add documents to help KrishvaQ understand this meeting.</p>
                      </div>
                    ) : (
                      <div className="ai-context-chip-list">
                        {selectedDocuments.map((document) => (
                          <div className="ai-context-chip" key={document.id}>
                            <span className="ai-context-chip-icon" aria-hidden="true">
                              <Icon name="file-text" size={18} />
                            </span>
                            <span className="ai-context-chip-copy">
                              <strong>{document.file_name}</strong>
                              <span>
                                {(document.file_type ||
                                  document.file_name.split('.').pop() ||
                                  'file'
                                ).toUpperCase()}
                              </span>
                            </span>
                            <button
                              type="button"
                              aria-label={`Remove ${document.file_name}`}
                              onClick={() => removeSelectedDocument(document.id)}
                            >
                              <Icon name="x" size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="ai-context-footer">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setIsDocumentSelectorOpen(true)}
                      className="ai-context-add-button"
                    >
                      <Icon name="plus" size={17} />
                      Add Documents
                    </Button>
                    <p>
                      KrishvaQ will use these documents to provide better meeting
                      assistance.
                    </p>
                  </div>
                </section>

                {isDocumentSelectorOpen && (
                  <DocumentSelector
                    selectedDocuments={selectedDocuments}
                    onChange={setSelectedDocuments}
                    onClose={() => setIsDocumentSelectorOpen(false)}
                  />
                )}
              </Field>
            </section>

            {error && <div className="form-alert">{error}</div>}

            {limitError && (
              <div className="form-alert">
                {canBuyMeeting ? "You've reached your monthly meeting limit." : limitError}{' '}
                {canBuyMeeting && addonOffer && (
                  <>
                    <Link to="/app/billing" className="form-alert-link">
                      Buy Extra Meetings
                      {addonOffer.addon_unit_price != null &&
                        ` (${formatAddonMoney(addonOffer.addon_unit_price, addonOffer.addon_currency)} each)`}
                    </Link>{' '}
                  </>
                )}
                {canBuyMeeting && (
                  <>
                    <Link to={PAY_PER_MEETING.buyPath} className="form-alert-link">
                      {passProduct ? payPerMeetingBuyLabel(passProduct) : PAY_PER_MEETING.cta}
                    </Link>{' '}
                  </>
                )}
                <Link to="/app/billing/plans" className="form-alert-link">
                  {canBuyMeeting ? 'View Plans' : 'Upgrade'}
                </Link>
              </div>
            )}

            <div className="start-meeting-submit-row">
              <Button type="submit" disabled={isSubmitting} className="start-meeting-submit">
                <Icon name="play" size={17} />
                {isSubmitting ? 'Starting...' : 'Start Meeting'}
              </Button>
            </div>
          </form>
        </Card>

        <aside className="start-meeting-side-panel">
          <div className="side-panel-header">
            <span className="panel-kicker">KrishvaQ readiness</span>
            <h3>What will activate</h3>
          </div>

          <div className="readiness-stack">
            <div className="readiness-item active">
              <span className="readiness-icon">
                <Icon name="mic" size={16} />
              </span>
              <div>
                <strong>Audio capture</strong>
                <p>Browser tab audio selected</p>
              </div>
            </div>

            <div className="readiness-item">
              <span className="readiness-icon">
                <Icon name="language" size={16} />
              </span>
              <div>
                <strong>Language intelligence</strong>
                <p>
                  {transcriptHidden
                    ? 'Speech is recognized for AI answers - live transcript hidden'
                    : isInterview
                      ? 'Interviewer speech is recognized to detect questions'
                      : 'Transcription and translation configured'}
                </p>
              </div>
            </div>

            <div className="readiness-item">
              <span className="readiness-icon">
                <Icon name="brain" size={16} />
              </span>
              <div>
                <strong>AI assistance</strong>
                <p>Answers, summaries, and follow-ups enabled</p>
              </div>
            </div>
          </div>

          <div className="meeting-flow-card">
            <div className="meeting-flow-line" aria-hidden="true" />
            <div>
              <span>1</span>
              <p>Start meeting</p>
            </div>
            <div>
              <span>2</span>
              <p>Select tab or microphone</p>
            </div>
            <div>
              <span>3</span>
              <p>Ask KrishvaQ during the conversation</p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default StartMeetingPage;
